import { z } from 'zod';
import {
  PHIWH,
  PHISight,
  PHITee,
  transformBodyMetricToPrimitive,
  transfromDateTimeToPrimitive,
  StudentPHIAllZod,
  StudentPHIAll,
  Student,
  StudentZod,
  PHIPhysical,
  StudentInjury,
  StudentInjuryZod,
  StudentHealthInfo,
  StudentHealthInfoZod,
  StudentWithClassGradeZod,
  StudentWithClassGrade,
  PHIBlood,
  PHIXRay,
  ExtractedPHIParasite,
  PHIWHZod,
  StudentGradeNoSeatYearSemZod,
  ExtractedPHIUrine,
  PHIWHSight,
  ClassSetting,
  StudentNameZod,
  SeriousInjury,
  StudentInjuryWithPid,
  StudentInjuryWithPidZod
} from '../model';
import { delet, get, post, put, download } from './BaseApi';
import { DateTime } from 'luxon';
import { loadTokenfromStorage, updateTokenExpireTime } from './TokenStorage';

const PHIWHListZod = z.array(PHIWHZod);
type PHIWHList = z.infer<typeof PHIWHListZod>;
const StudentGradeNoSeatYearSemListZod = z.array(StudentGradeNoSeatYearSemZod);
const StudentNameListZod = z.array(StudentNameZod);
const StudentInjuryListWithPidZod = z.array(StudentInjuryWithPidZod);
const htmlZod = z.string();
const uploadZod = z.object({
  success: z.boolean(),
  payload: z.any().optional(),
  data: z.any().optional(),
  errors: z.any().optional(),
});
type uploadType = z.infer<typeof uploadZod>;
type StudentGradeNoSeatYearSemList = z.infer<
  typeof StudentGradeNoSeatYearSemListZod
>;
type StudentNameList = z.infer<typeof StudentNameListZod>;
type StudentInjuryListWithPid = z.infer<typeof StudentInjuryListWithPidZod>;

export async function InsertStudentBodyMetrix(
  id: string,
  metric: PHIWH
): Promise<void> {
  await put(
    `/api/student/${id}/metric`,
    transformBodyMetricToPrimitive(metric)
  );
}
export async function UpdateWHExamDate(
  classId: string,
  year: number,
  sem: number,
  initialExamDate: DateTime,
  newExamDate: DateTime
): Promise<number> {
  return await put(`/api/student/metric/update/examDate`, z.number(), {
    classId,
    year,
    sem,
    initialExamDate: initialExamDate.toISO(),
    newExamDate: newExamDate.toISO(),
  });
}
export async function UpdateSelectedWHExamDate(
  classId: string,
  year: number,
  sem: number,
  initialExamDate: DateTime,
  newExamDate: DateTime
): Promise<number> {
  return await put(`/api/student/metric/update/byExamDate`, z.number(), {
    classId,
    year,
    sem,
    initialExamDate: initialExamDate.toISO(),
    newExamDate: newExamDate.toISO(),
  });
}

export async function InsertStudentSight(
  id: string,
  sight: PHISight
): Promise<void> {
  await put(
    `/api/student/${id}/phi/sight`,
    transfromDateTimeToPrimitive(sight)
  );
}
export async function InsertStudentWHSight(
  id: string,
  sight: PHIWHSight
): Promise<void> {
  await put(
    `/api/student/${id}/phi/whsight`,
    transfromDateTimeToPrimitive(sight)
  );
}
export async function InsertStudentTee(id: string, tee: PHITee): Promise<void> {
  await put(`/api/student/${id}/phi/tee`, transfromDateTimeToPrimitive(tee));
}

export async function InsertStudentPhysical(
  id: string,
  physical: PHIPhysical
): Promise<void> {
  await put(
    `/api/student/${id}/phi/physical`,
    transfromDateTimeToPrimitive(physical)
  );
}

export async function InsertStudentBlood(
  id: string,
  blood: PHIBlood
): Promise<void> {
  await put(
    `/api/student/${id}/phi/blood`,
    transfromDateTimeToPrimitive(blood)
  );
}
export async function InsertStudentXRay(
  id: string,
  xray: PHIXRay
): Promise<void> {
  await put(`/api/student/${id}/phi/xray`, transfromDateTimeToPrimitive(xray));
}
export async function InsertStudentUrine(
  id: string,
  urine: ExtractedPHIUrine
): Promise<void> {
  await put(
    `/api/student/${id}/phi/urine`,
    transfromDateTimeToPrimitive(urine)
  );
}
export async function InsertStudentParasite(
  id: string,
  parasite: ExtractedPHIParasite
): Promise<void> {
  await put(
    `/api/student/${id}/phi/parasite`,
    transfromDateTimeToPrimitive(parasite)
  );
}

export async function GetStudentWithPHIAll(
  id: string,
  graduatedYear?: number
): Promise<StudentPHIAll> {
  return graduatedYear
    ? await get(`/api/student/${id}/phi`, StudentPHIAllZod, { graduatedYear })
    : await get(`/api/student/${id}/phi`, StudentPHIAllZod);
}

export async function GetStudentWithPHIAllByPid(
  id: string
): Promise<StudentPHIAll> {
  return await get(`/api/student/pid/${id}/phi`, StudentPHIAllZod);
}

export async function GetBodyMatrixHistory(
  id: string,
  year: number,
  grade: number
): Promise<PHIWHList> {
  return await get(`/api/student/${id}/phi/wh/history`, PHIWHListZod, {
    year,
    grade,
  });
}

export async function GetStudent(
  id: string,
  year?: number,
  sem?: number
): Promise<Student> {
  return year || sem
    ? await get(`/api/student/${id}`, StudentZod, { year, sem })
    : await get(`/api/student/${id}`, StudentZod);
}
export async function GetStudentByGradeNoSeat(
  grade: number,
  no: number,
  seat: number,
  year?: number,
  sem?: number
): Promise<Student> {
  return year || sem
    ? await get(`/api/student/gradenoseat/${grade}/${no}/${seat}`, StudentZod, {
        year,
        sem,
      })
    : await get(`/api/student/gradenoseat/${grade}/${no}/${seat}`, StudentZod);
}

export async function GetStudentBySid(sid: string): Promise<Student> {
  return await get(`/api/student/sid/${sid}`, StudentZod);
}

export async function GetStudentWithClass(
  id: string,
  year: number,
  sem: number
): Promise<StudentWithClassGrade> {
  return await get(`/api/student/${id}/withclass`, StudentWithClassGradeZod, {
    year,
    sem,
  });
}

export async function SetStudent(
  student: Student,
  year?: number,
  sem?: number,
  no?: number
): Promise<void> {
  year || sem
    ? await post(`/api/student/${student.pid}`, student, { year, sem, no })
    : await post(`/api/student/${student.pid}`, student);
}

export async function SetStudentRemark(
  pid: string,
  remark: string,
  year?: number,
  sem?: number
): Promise<void> {
  year || sem
    ? await post(`/api/student/${pid}/remark/update`, { remark }, { year, sem })
    : await post(`/api/student/${pid}/remark/update`, { remark });
}

export async function CreateStudent(
  student: Student,
  year?: number,
  sem?: number,
  grade?: number,
  no?: number,
  className?: string,
  classId?: string
): Promise<void> {
  year || sem
    ? await post(`/api/student/${student.pid}/create`, student, {
        year,
        sem,
        grade,
        no,
        className,
        classId,
      })
    : await post(`/api/student/${student.pid}/create`, student);
}

const StudentInjuriesZod = z.array(StudentInjuryZod);
export async function GetStudentInjuries(
  studentId: string
): Promise<StudentInjury[]> {
  return await get(`/api/student/${studentId}/injuries`, StudentInjuriesZod);
}

export async function InsertStudentInjuries(
  studentId: string,
  injury: StudentInjury
): Promise<void> {
  await put(`/api/student/${studentId}/injuries`, injury);
}

export async function UpdateStudentInjuries(
  studentId: string,
  injury: StudentInjury
): Promise<void> {
  await post(`/api/student/${studentId}/injuries`, injury);
}

export async function RemoveStudentInjuries(
  studentId: string,
  injuryId: number
): Promise<void> {
  await delet(`/api/student/${studentId}/injuries/${injuryId}`);
}

export async function InsertSeriousInjuries(
  studentId: string,
  injury: SeriousInjury
): Promise<void> {
  await put(`/api/student/${studentId}/seriousinjuries`, injury);
}

export async function UpdateSeriousInjuries(
  studentId: string,
  injury: SeriousInjury
): Promise<void> {
  await post(`/api/student/${studentId}/seriousinjuries`, injury);
}

export async function RemoveSeriousInjuries(
  studentId: string,
  injuryId: number
): Promise<void> {
  await delet(`/api/student/${studentId}/seriousinjuries/${injuryId}`);
}

export async function GetHealthInfo(
  studentId: string
): Promise<StudentHealthInfo> {
  return await get(
    `/api/student/${studentId}/health-info`,
    StudentHealthInfoZod
  );
}

export async function deleteStudentWH(
  pid: string,
  year: number,
  sem: number
): Promise<void> {
  await delet(`/api/student/${pid}/phi/wh`, {}, { year, sem });
}

export async function deleteStudentSight(
  pid: string,
  year: number,
  sem: number
): Promise<void> {
  await delet(`/api/student/${pid}/phi/sight`, {}, { year, sem });
}

export async function deleteStudentWHSight(
  pid: string,
  year: number,
  sem: number
): Promise<void> {
  await delet(`/api/student/${pid}/phi/whsight`, {}, { year, sem });
}

export async function deleteStudentTee(
  pid: string,
  year: number,
  sem: number
): Promise<void> {
  await delet(`/api/student/${pid}/phi/tee`, {}, { year, sem });
}

export async function deleteStudentPhysical(
  pid: string,
  year: number,
  sem: number
): Promise<void> {
  await delet(`/api/student/${pid}/phi/physical`, {}, { year, sem });
}

export async function deleteStudentBlood(
  pid: string,
  year: number,
  sem: number
): Promise<void> {
  await delet(`/api/student/${pid}/phi/blood`, {}, { year, sem });
}

export async function deleteStudentXRay(
  pid: string,
  year: number,
  sem: number
): Promise<void> {
  await delet(`/api/student/${pid}/phi/xray`, {}, { year, sem });
}

export async function deleteStudentParasite(
  pid: string,
  year: number,
  sem: number
): Promise<void> {
  await delet(`/api/student/${pid}/phi/parasite`, {}, { year, sem });
}

export async function deleteStudentUrine(
  pid: string,
  year: number,
  sem: number
): Promise<void> {
  await delet(`/api/student/${pid}/phi/urine`, {}, { year, sem });
}

export async function getStudentClasses(
  pid: string,
  year: number
): Promise<StudentGradeNoSeatYearSemList> {
  return await get(
    `/api/student/${pid}/classes`,
    StudentGradeNoSeatYearSemListZod,
    { year }
  );
}

export async function getStudentNameList(
  classes: ClassSetting[],
  year: number,
  sem: number
): Promise<StudentNameList> {
  return await post('/api/students/namelist', StudentNameListZod, {
    classes,
    year,
    sem,
  });
}
export async function getGraduatedStudentNameList(
  classes: ClassSetting[],
  year: number,
  sem: number
): Promise<StudentNameList> {
  return await post('/api/students/namelist/graduated', StudentNameListZod, {
    classes,
    year,
    sem,
  });
}

export async function uploadSemesterHealthExam(
  year: number,
  sem: number
): Promise<uploadType> {
  return await post(
    `/api/students/upload/${year}/semester/${sem}/health`,
    uploadZod
  );
}

export async function getSemesterHealthExamResult(
  year: number,
  sem: number
): Promise<string> {
  return await post(
    `/api/students/upload/${year}/semester/${sem}/health/result`,
    htmlZod
  );
}

export async function downloadStudentData(
  pid: string,
  birth: DateTime,
  data: {
    sid: string;
    seat: number;
    year: number;
    sem: number;
    classGrade: number;
    classNo: number;
  }
): Promise<void> {
  const encodedPid = encodeURIComponent(pid);
  const encodedBirth = encodeURIComponent(birth.toISO()!);

  await post(
    `/api/students/download/${encodedPid}/health?birth=${encodedBirth}`,
    data
  );
}

export async function downloadInjuryExcel(
  schoolName: string,
  year: number,
  sem: number
): Promise<Blob> {
  return await download('/api/pdf/injury', {
    schoolName,
    year,
    sem,
  });
}

export function downloadAllInjuriesExcel(
  schoolId: string
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.open(
      "GET",
      `/api/students/injuries/${schoolId}/download-all`,
      true
    );

    xhr.responseType = "blob";

    xhr.onload = async () => {
      const contentType =
        xhr.getResponseHeader("content-type") || "";

      if (xhr.status < 200 || xhr.status >= 300) {
        const message = await xhr.response.text();

        reject(new Error(message || "下載失敗"));
        return;
      }

      // 後端回「無檔案 / 無權限」文字
      if (
        contentType.includes("text/plain") ||
        contentType.includes("application/json")
      ) {
        const message = await xhr.response.text();

        reject(new Error(message || "下載失敗"));
        return;
      }

      resolve(xhr.response);
    };

    xhr.onerror = () => {
      reject(new Error("下載失敗"));
    };

    xhr.send();
  });
}